'use client'

import React, { ReactNode } from 'react'

export interface CardSelectOption<TValue extends string | number = string | number> {
    label: string
    value: TValue
    icon?: ReactNode
}

interface CardSelectProps<TValue extends string | number = string | number> {
    options: CardSelectOption<TValue>[]
    value: TValue | null | undefined
    onChange: (value: TValue, option: CardSelectOption<TValue>) => void
    /** Tailwind grid class override (defaults to 1 col mobile / 2 col desktop) */
    containerClassName?: string
    /** data-test prefix for each card. Defaults to `form-select-option`,
     *  mirroring Vue's FormSelect.vue (`:data-test="`form-select-option-${value}`"`).
     *  Pass `select-card` for landing/family-type-style cards (Vue's SelectCard). */
    testIdPrefix?: string
    error?: { message?: string }
    /** Accent colour of the selected/hover state. Defaults to the historic blue. */
    variant?: 'blue' | 'travel'
}

export default function CardSelect<TValue extends string | number = string | number>({
    options,
    value,
    onChange,
    containerClassName = 'grid grid-cols-1 sm:grid-cols-2 gap-2',
    testIdPrefix = 'form-select-option',
    error,
    variant = 'blue',
}: CardSelectProps<TValue>) {
    const isTravel = variant === 'travel'
    // En erreur (aucun choix), les cartes prennent la bordure rouge des autres champs du formulaire
    const hasError = !!error?.message
    const selectedClass = isTravel
        ? 'rounded-[14px] border-2 border-rm-blue bg-rm-blue/10 text-pale'
        : 'rounded-rm-10 border border-blue-500 bg-rm-blue-pale text-pale'
    const idleClass = isTravel
        ? `rounded-[14px] border-2 bg-white text-pale ${hasError ? 'border-invalidRed' : 'border-[#EFE6E2] hover:border-rm-gray'}`
        : `rounded-rm-10 border bg-white text-pale ${hasError ? 'border-invalidRed' : 'border-gray-200 hover:border-blue-300'}`
    const layoutClass = isTravel
        ? 'flex items-center min-h-[52px] px-4 py-3 text-left'
        : 'flex items-center min-h-12 h-[50px] pl-5 pr-3 text-left'

    return (
        <div>
            <div className={containerClassName}>
                {options.map((option) => {
                    const isSelected = option.value === value
                    return (
                        <button
                            type="button"
                            key={String(option.value)}
                            data-test={`${testIdPrefix}-${option.value}`}
                            onClick={() => onChange(option.value, option)}
                            className={`group transition-colors duration-200 cursor-pointer noselect focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-blue ${layoutClass}
                                ${isSelected ? selectedClass : idleClass}`}
                            aria-pressed={isSelected}
                        >
                            {option.icon ? (
                                <span
                                    className={`mr-3 text-[14px] leading-none flex items-center justify-center w-5 shrink-0 ${
                                        isSelected ? 'text-rm-blue-accent' : 'text-rm-gray-light'
                                    }`}
                                >
                                    {option.icon}
                                </span>
                            ) : null}
                            <span
                                className={
                                    isTravel
                                        ? `text-[15px] sm:text-base ${isSelected ? 'font-bold' : 'font-normal'}`
                                        : 'text-[14px] font-medium'
                                }
                            >
                                {option.label}
                            </span>
                            {!option.icon && (
                                <span
                                    aria-hidden
                                    className={`ml-auto flex size-5 shrink-0 items-center justify-center rounded-full ${
                                        isSelected
                                            ? isTravel
                                                ? 'bg-rm-blue'
                                                : 'bg-blue-500'
                                            : `border-2 bg-white ${isTravel ? 'border-[#DFD5D1]' : 'border-rm-gray-light'}`
                                    }`}
                                >
                                    {isSelected && <i className="fa-regular fa-check text-[9px] text-white" />}
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>
            {error?.message && (
                <p className="mt-1 text-[12px] text-red-600">{error.message}</p>
            )}
        </div>
    )
}
